import {
  readCachedTenantToken,
  tokenNeedsRefresh,
  writeCachedTenantToken,
} from '@/helpers/supportChatWidgetToken'
import { mintSupportQueryToken } from '@/requests/support-query'
import type { CustomerType } from '@/types'
import type {
  CuracelChatWidgetApi,
  SupportChatWidgetCustomer,
} from '@/types/support-query'
import { captureException } from '@sentry/vue'
import { isAxiosError } from 'axios'

const DEFAULT_SUPPORT_WIDGET_ORIGIN = 'https://support.curacel.ai'
const SUPPORT_WIDGET_SCRIPT_ID = 'curacel-support-widget-script'
const WIDGET_LOAD_TIMEOUT_MS = 5_000
const WIDGET_LOAD_POLL_INTERVAL_MS = 50
const TOKEN_REFRESH_LEAD_MS = 60_000
const TOKEN_RETRY_DELAY_MS = 30_000
const TOKEN_THROTTLE_DELAY_MS = 60_000

let tenantTokenRefreshTimer: number | null = null
let tenantTokenRefreshGeneration = 0

function clearTenantTokenRefresh(): void {
  if (tenantTokenRefreshTimer === null) {
    return
  }

  window.clearTimeout(tenantTokenRefreshTimer)
  tenantTokenRefreshTimer = null
}

export function teardownSupportChatWidget(): void {
  clearTenantTokenRefresh()
  tenantTokenRefreshGeneration++
}

function supportWidgetOrigin(): string {
  return (
    import.meta.env.VITE_SUPPORT_WIDGET_ORIGIN || DEFAULT_SUPPORT_WIDGET_ORIGIN
  ).replace(/\/+$/, '')
}

function loadSupportChatWidgetScript(): void {
  if (document.getElementById(SUPPORT_WIDGET_SCRIPT_ID)) {
    return
  }

  const script = document.createElement('script')
  script.id = SUPPORT_WIDGET_SCRIPT_ID
  script.src = `${supportWidgetOrigin()}/widget/chat-widget.js`
  script.dataset.businessSlug = 'curacel-ai'
  script.dataset.position = 'bottom-right'
  script.dataset.theme = 'light'
  script.dataset.language = 'auto'
  script.async = true
  document.body.appendChild(script)
}

function waitForSupportChatWidget(): Promise<CuracelChatWidgetApi | null> {
  if (window.CuracelChatWidget) {
    return Promise.resolve(window.CuracelChatWidget)
  }

  loadSupportChatWidgetScript()

  return new Promise((resolve) => {
    const deadline = Date.now() + WIDGET_LOAD_TIMEOUT_MS

    const checkForWidget = (): void => {
      if (window.CuracelChatWidget) {
        resolve(window.CuracelChatWidget)
        return
      }

      if (Date.now() >= deadline) {
        resolve(null)
        return
      }

      window.setTimeout(checkForWidget, WIDGET_LOAD_POLL_INTERVAL_MS)
    }

    window.setTimeout(checkForWidget, WIDGET_LOAD_POLL_INTERVAL_MS)
  })
}

function widgetCustomer(user: CustomerType): SupportChatWidgetCustomer {
  return {
    external_id: String(user.id),
    name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
    email: user.email ?? '',
    phone: user.phone ?? null,
  }
}

function isFeatureDenied(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403
}

function isThrottled(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 429
}

function reportTokenError(error: unknown): void {
  if (!isFeatureDenied(error) && !isThrottled(error)) {
    captureException(error)
  }
}

function retryDelayForError(error: unknown): number {
  if (!isAxiosError(error) || error.response?.status !== 429) {
    return TOKEN_RETRY_DELAY_MS
  }

  const retryAfter = error.response?.headers?.['retry-after']
  const retryAfterSeconds = Number(retryAfter)

  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? retryAfterSeconds * 1_000
    : TOKEN_THROTTLE_DELAY_MS
}

function scheduleTenantTokenRefresh(
  widget: CuracelChatWidgetApi,
  userId: string,
  expiresAt: string,
  generation: number,
): void {
  if (generation !== tenantTokenRefreshGeneration) {
    return
  }

  const expiryTime = Date.parse(expiresAt)

  if (Number.isNaN(expiryTime)) {
    captureException(new Error('Support query token has an invalid expiry'))
    return
  }

  clearTenantTokenRefresh()
  const delay = Math.max(1_000, expiryTime - Date.now() - TOKEN_REFRESH_LEAD_MS)

  tenantTokenRefreshTimer = window.setTimeout(() => {
    void refreshTenantToken(widget, userId, generation)
  }, delay)
}

function scheduleTenantTokenRetry(
  widget: CuracelChatWidgetApi,
  userId: string,
  generation: number,
  delay: number,
): void {
  if (generation !== tenantTokenRefreshGeneration) {
    return
  }

  clearTenantTokenRefresh()
  tenantTokenRefreshTimer = window.setTimeout(() => {
    void refreshTenantToken(widget, userId, generation)
  }, delay)
}

async function refreshTenantToken(
  widget: CuracelChatWidgetApi,
  userId: string,
  generation: number,
): Promise<void> {
  const cached = readCachedTenantToken(userId)

  if (cached && !tokenNeedsRefresh(cached.expiresAt, TOKEN_REFRESH_LEAD_MS)) {
    if (generation !== tenantTokenRefreshGeneration) {
      return
    }

    widget.setTenantContext(cached.token)
    scheduleTenantTokenRefresh(widget, userId, cached.expiresAt, generation)
    return
  }

  try {
    const response = await mintSupportQueryToken()

    if (generation !== tenantTokenRefreshGeneration) {
      return
    }

    writeCachedTenantToken(userId, response.token, response.expires_at)
    widget.setTenantContext(response.token)
    scheduleTenantTokenRefresh(widget, userId, response.expires_at, generation)
  } catch (error) {
    if (generation !== tenantTokenRefreshGeneration) {
      return
    }

    reportTokenError(error)

    if (!isFeatureDenied(error)) {
      scheduleTenantTokenRetry(
        widget,
        userId,
        generation,
        retryDelayForError(error),
      )
    }
  }
}

export async function initializeSupportChatWidget(
  user: CustomerType,
): Promise<void> {
  teardownSupportChatWidget()
  const generation = tenantTokenRefreshGeneration
  const widget = await waitForSupportChatWidget()

  if (!widget || generation !== tenantTokenRefreshGeneration) {
    return
  }

  const userId = String(user.id)
  const customer = widgetCustomer(user)
  const contextTags = { product: 'auto_claims' } as const
  const cached = readCachedTenantToken(userId)

  if (cached && !tokenNeedsRefresh(cached.expiresAt, TOKEN_REFRESH_LEAD_MS)) {
    widget.init(customer, contextTags, cached.token)
    scheduleTenantTokenRefresh(widget, userId, cached.expiresAt, generation)
    return
  }

  try {
    const response = await mintSupportQueryToken()

    if (generation !== tenantTokenRefreshGeneration) {
      return
    }

    writeCachedTenantToken(userId, response.token, response.expires_at)
    widget.init(customer, contextTags, response.token)
    scheduleTenantTokenRefresh(widget, userId, response.expires_at, generation)
  } catch (error) {
    if (generation !== tenantTokenRefreshGeneration) {
      return
    }

    reportTokenError(error)
    widget.init(customer, contextTags)

    if (!isFeatureDenied(error)) {
      scheduleTenantTokenRetry(
        widget,
        userId,
        generation,
        retryDelayForError(error),
      )
    }
  }
}
